import React, { useEffect } from "react";
import { Button } from "antd";
import { GroundTourTransaction } from "../../../interfaces/booking.interface";
import { humanize } from "../../../utils/helpers";
import useBreakpoint from "../../../utils/useBreakpoint";
import { CheckmarkSVG } from "../../../components/shared/v3/PaymentSuccessV3";
import { PAX_TYPES } from "./components/ChooseYourTour";

type Status = "paid" | "pending" | "failed";

const STATUS: Record<Status, { icon: React.ReactNode; title: string; color: string; message: React.ReactNode }> = {
  paid: {
    icon: <CheckmarkSVG />,
    title: "Payment Successful",
    color: "#00BD65",
    message: <>Thank you for your booking. <br />A confirmation email has been sent to your inbox.</>,
  },
  pending: {
    icon: <img src="/iceholidays-assets/images/v3/payment-pending.svg" alt="Pending" />,
    title: "Pending Payment",
    color: "#DD741D",
    message: "Payment is currently pending. Your booking will not be confirmed until the payment is successfully processed.",
  },
  failed: {
    icon: <img src="/iceholidays-assets/images/v3/payment-failed.svg" alt="Failed" />,
    title: "Payment Failed",
    color: "#DC1A22",
    message: <>Your payment could not be completed. <br />Your booking has not been confirmed. Please retry the payment process to secure your reservation.</>,
  },
};

const HOLD_NOTICE =
  "Your payment will be held for up to 72 hours while we confirm hotel and ground-service availability. If we are unable to confirm your booking, it will be cancelled and your payment will be refunded.";

const money = (currency: string, value: number) =>
  `${currency} ${value.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const categoryLabel = (key: string) => PAX_TYPES.find((type) => type.key === key)?.label ?? humanize(key);

interface Props {
  transaction: GroundTourTransaction;
  countdown: number;
  onRetry: () => void;
}

// Result page of a B2C2B Ground Tour payment (issue #765).
export default function GroundTourPaymentStatus({ transaction, countdown, onRetry }: Props) {
  const isMobile = useBreakpoint();
  const status = (STATUS[transaction.status as Status] ? transaction.status : "failed") as Status;
  const { icon, title, color, message } = STATUS[status];
  const { currency } = transaction;

  // The mobile layout hides the site header/footer (see .ground-payment-status-page in v2.scss).
  useEffect(() => {
    document.body.classList.add("ground-payment-status-page");
    return () => document.body.classList.remove("ground-payment-status-page");
  }, []);

  const dates = transaction.returnDate
    ? `${transaction.departureDate} - ${transaction.returnDate}`
    : transaction.departureDate;
  const showHoldNotice = status === "paid" && transaction.hasDetails && !transaction.instantConfirmation;
  const totalLabel = isMobile && status === "paid" ? "Payment Paid" : "Grand Total";

  const countdownText = (
    <>We're updating your payment status. Please retry in {countdown} seconds...</>
  );

  const backButton = (
    <Button
      key="home"
      href="/web"
      color="primary"
      variant={status === "failed" ? "outlined" : "solid"}
      size={isMobile ? "large" : "middle"}
    >
      Back to Home
    </Button>
  );
  const retryButton = (
    <Button key="retry" onClick={onRetry} color="primary" variant="solid" size={isMobile ? "large" : "middle"}>
      Retry Payment
    </Button>
  );
  // Desktop: Retry first. Mobile bar: Back to Home on the left, Retry on the right (Figma).
  const desktopButtons = status === "failed" ? [retryButton, backButton] : [backButton];
  const mobileButtons = status === "failed" ? [backButton, retryButton] : [backButton];

  return (
    <div className="ground-payment-status">
      <div className="ground-payment-status__main">
        <section className="ground-payment-status__result" aria-live="polite">
          <div className="ground-payment-status__icon">{icon}</div>
          <h1 className="ground-payment-status__title" style={{ color }}>{title}</h1>
          <div className="ground-payment-status__amount">{money(currency, transaction.amountPaid)}</div>
          <div className="ground-payment-status__date">{transaction.transactionDate}</div>
          <p className="ground-payment-status__message">{message}</p>
          {status === "pending" && <div className="ground-payment-status__countdown ground-payment-status__countdown--mobile">{countdownText}</div>}
          {showHoldNotice && <div className="ground-payment-status__notice">{HOLD_NOTICE}</div>}

          {transaction.hasDetails && (
            <div className="ground-payment-status__card">
              <h2 className="ground-payment-status__card-title">{transaction.tourTitle}</h2>
              <dl className="ground-payment-status__rows">
                <div><dt>Reference No.</dt><dd>{transaction.refNo}</dd></div>
                <div><dt>Payment Method</dt><dd>{transaction.paymentMethod}</dd></div>
                <div>
                  <dt>Tour Code</dt>
                  <dd>
                    {transaction.tourCode}
                    {transaction.tourTag && <span className="ground-payment-status__tag">{transaction.tourTag}</span>}
                  </dd>
                </div>
                <div><dt>Departure Date</dt><dd>{dates}</dd></div>
                <div><dt>Room</dt><dd>{transaction.rooms}</dd></div>
                <div><dt>Tour Language</dt><dd>{transaction.language}</dd></div>
              </dl>

              <div className="ground-payment-status__fare">
                <h3>Fare Breakdown</h3>
                <div className="ground-payment-status__fare-label">{transaction.fareLabel}</div>
                {transaction.fareItems.map((item) => (
                  <div key={item.id} className="ground-payment-status__fare-line">
                    <span>{categoryLabel(item.category)} x {item.count}</span>
                    <span>{money(currency, item.sum)}</span>
                  </div>
                ))}
                {transaction.otherCharges > 0.005 && (
                  <div className="ground-payment-status__fare-line">
                    <span>Surcharges &amp; other charges</span>
                    <span>{money(currency, transaction.otherCharges)}</span>
                  </div>
              )}
              {transaction.adminFee > 0 && (
                <div className="ground-payment-status__fare-line">
                  <span>Admin Fee</span>
                  <span>{money(currency, transaction.adminFee)}</span>
                </div>
              )}
              <div className="ground-payment-status__fare-total">
                <span>{totalLabel}</span>
                <span>{money(currency, transaction.grandTotal)}</span>
              </div>
            </div>
          </div>
          )}

          <div className="ground-payment-status__actions">
            {status === "pending" && <div className="ground-payment-status__countdown">{countdownText}</div>}
            <div className="ground-payment-status__buttons">{desktopButtons}</div>
          </div>
        </section>
      </div>

      <div className="ground-payment-status__bar">{mobileButtons}</div>
    </div>
  );
}
